import {FaPlus} from "react-icons/fa";
import Button from "../../../../../components/button/button";
import {Template} from "../../../../../components/template/template";
import {Card} from "../../../../../components/card/card";
import InputForm from "../../../../../components/input/input-form";
import SelectComponent from "../../../../../components/select/select-component";
import {useEffect, useMemo, useState} from "react";
import ProjectUtils from "../../../../../utils/project-utils";
import projectUtils from "../../../../../utils/project-utils";
import NotificationVisibilityService from "../../../../../services/web/notification-visibility-service";
import {navigateHook} from "../../../../../hooks/use-navigate";
import {useParams} from "react-router";
import {useMutation, useQuery} from "@tanstack/react-query";
import Swal from "sweetalert2";

const NotificationsVisibilityForm = () => {
    const navigate = navigateHook();
    const { id } = useParams();
    const currentId = id ? Number(id) : null;

    const [origin, setOrigin] = useState("");
    const [selectedGroupsId, setSelectedGroupsId] = useState([]);

    const { data: groupOptions = [], isLoading: isLoadingGroups, } = useQuery({
        queryKey: ["notificationVisibilityGroupOptions"],
        queryFn: NotificationVisibilityService.listGroupOptions,
        select: (data) => (Array.isArray(data) ? data : [])
            .filter((g) => g?.status !== false)
            .map((g) => ({ label: g.text ?? g.name, value: String(g.id) })),
    });

    const { data: formData, isLoading: isLoadingForm, refetch } = useQuery({
        queryKey: ["notificationVisibility", currentId],
        enabled: !!currentId,
        queryFn: () => NotificationVisibilityService.getById(currentId)
    });

    useEffect(() => {
        if (!currentId || !formData) return;

        setOrigin(formData?.origin ?? "");
        setSelectedGroupsId(Array.isArray(formData?.groups) ? formData.groups.map(Number) : []);
    }, [currentId, formData]);

    const selectedGroupsValue = useMemo(() => {
        const ids = new Set(selectedGroupsId.map(Number));
        return groupOptions.filter((o) => ids.has(Number(o.value)));
    }, [groupOptions, selectedGroupsId]);

    const validate = () => {
        if (!origin?.trim()) return 'O campo "Origem" é obrigatório.';
        if (!selectedGroupsId?.length) return 'O campo "Grupos de operação" é obrigatório.';
    };

    const saveMutation = useMutation({
        mutationFn: async ({ addNew = false} = {}) => {
            const err = validate();
            if (err) throw new Error(err);
            
            const payload = {
                origin: origin.trim(),
                groups: selectedGroupsId,
            };
            
            const response =  currentId
            ? await NotificationVisibilityService.update(currentId, payload)
            : await NotificationVisibilityService.create(payload);

            return { response, addNew };
        },
        onSuccess: ({ response, addNew }) => {
            ProjectUtils.success(!currentId);
            
            const newId = response?.id ?? response?.data ?? response;

            if (addNew) {
                newVisibility();
                return;
            }
            
            if (!currentId) {
                const urlDev = `/notifications-visibility/form/${newId}`;
                navigate(urlDev);
                return;
            }
            refetch?.();
        },
        onError: (err) => {
            const msg = err?.message?.includes("O campo") ? err.message: "Não foi possível salvar.";

            ProjectUtils.swalErrorMessage("", msg, "Ok");
        },
    });
    
    const isLoading = isLoadingGroups || (currentId && isLoadingForm) || saveMutation.isPending;

    const handleDelete = () => {
        if (!currentId) return;

        Swal.fire({
            title: "Excluir",
            text: `Excluir a visibilidade "${origin}?"`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#063A6C",
            confirmButtonText: "Sim",
            cancelButtonColor: "#c10007",
            cancelButtonText: "Não",
            reverseButtons: true,
        }).then((result) => {
            if (result.isConfirmed) {
                return NotificationVisibilityService
                    .delete(currentId)
                    .then(() => {
                        ProjectUtils.success(true, "Excluído com sucesso");
                        const urlDev = "/notifications-visibility";
                        navigate(urlDev);
                    })
                    .catch(() => {
                        ProjectUtils.swalErrorMessage("", "Erro ao excluir", "Ok");
                    })
            }
        });
    };

    const newVisibility = () => {
        setOrigin("");
        setSelectedGroupsId([]);
        const urlDev = "/notifications-visibility/form";
        navigate(urlDev);
    }

    return (
        <Template isLoading={isLoading}>
            <Template.Header>
                <Template.Title>Visibilidade</Template.Title>
                {currentId && (
                    <Button onClick={newVisibility} target="primary-outline">
                        <FaPlus />Adicionar visibilidade
                    </Button>
                )}
            </Template.Header>
            <Template.Body className="space-y-2">
                <Card>
                    <Card.Body>
                        <div className="flex gap-4">
                            <InputForm
                                id="origin"
                                label="Origem *"
                                value={origin}
                                onChange={(e) => setOrigin(e.target.value)}
                                classNameWrapper="flex-1"
                            />
                            <SelectComponent
                                id="groupOptions"
                                label="Grupos de operação *"
                                isMulti
                                value={selectedGroupsValue}
                                options={groupOptions}
                                onChange={(v) => setSelectedGroupsId((v ?? []).map((x) => Number(x.value)))}
                                classNameWrapper="flex-1"
                            />
                        </div>
                    </Card.Body>
                </Card>
                <Card>
                    <Card.Body className="flex gap-2 justify-end">
                        {currentId && (
                            <Button onClick={handleDelete} target="danger" actionFeedbackType='delete'>Excluir</Button>
                        )}
                        <Button target="primary-outline" onClick={() => saveMutation.mutate({ addNew: true })} actionFeedbackType='save'>Salvar e adicionar novo</Button>
                        <Button onClick={() => saveMutation.mutate({ addNew: false})} actionFeedbackType='save'>Salvar</Button>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>        
    )
}

export default NotificationsVisibilityForm;